import { useCallback, useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { useForm } from 'react-final-form';

import { ToggleButtonGroupField } from '@occc-app/design-system';
import { Typography } from '@occc/ui-design-system';
import { CustomSectionContext } from '@occc-app/offerHelpers/TargetForm/CustomSection';
import { PromocodesOfferType, OfferFragment } from '@occc/graphql';

import { PromocodesGlobalConfiguration } from '../PromocodesConfiguration/promocodes-global-configuration';
import { PromocodesIndividualConfiguration } from '../PromocodesConfiguration/promocodes-individual-configuration';
import {
  PromocodesConfigureFormValues,
  PromocodesTargetFormValues,
} from '../PromocodesConfiguration/promocodes-configuration.types';
import { getPromocodesTargetInitialValues } from '../PromocodesConfiguration/promocodes-configuration.utils';
import { PROMOCODES_CODES_TYPE_OPTIONS } from '../PromocodesConfiguration/promocodes-configuration.constants';
import { useSavePromocodesCodesType } from '../../hooks/use-save-promocodes-codes-type.hook';

type PromocodesTargetFormContainerProps = CustomSectionContext & {
  offer?: OfferFragment | null;
};

export const PromocodesTargetFormContainer = ({
  values,
  offer,
  offerId,
  onRefetchOffer,
  isRefetching,
}: PromocodesTargetFormContainerProps) => {
  const form = useForm();
  const saveCodesType = useSavePromocodesCodesType();
  const syncedOfferKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!offer) {
      return;
    }

    const promocodesFields = getPromocodesTargetInitialValues(offer);
    const syncKey = JSON.stringify(promocodesFields);

    if (syncedOfferKeyRef.current === syncKey) {
      return;
    }

    syncedOfferKeyRef.current = syncKey;

    if (promocodesFields.promocodes?.codesType === PromocodesOfferType.Individual) {
      form.change('promoCodeGroups', promocodesFields.promoCodeGroups ?? []);
      form.change('promocodes', promocodesFields.promocodes);
      return;
    }

    form.initialize({
      ...form.getState().values,
      ...promocodesFields,
    });
  }, [offer, form]);

  const targetValues = values as unknown as PromocodesTargetFormValues;
  const codesType = targetValues.promocodes?.codesType ?? PromocodesOfferType.Global;
  const promoCodeGroups = targetValues.promoCodeGroups ?? [];

  const handleBeforeFileUpload = useCallback(async () => {
    await saveCodesType(offer, PromocodesOfferType.Individual);
    form.mutators.setFieldValue('promocodes.codesType', PromocodesOfferType.Individual);
  }, [form.mutators, offer, saveCodesType]);

  const handleOfferPromocodesChanged = useCallback(async () => {
    await onRefetchOffer(true);
  }, [onRefetchOffer]);

  const configurationValues = {
    promocodes: targetValues.promocodes,
  } as PromocodesConfigureFormValues;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant='h6' paragraph>
          Promo Codes Type
        </Typography>
        <ToggleButtonGroupField
          name='promocodes.codesType'
          buttons={PROMOCODES_CODES_TYPE_OPTIONS}
        />
      </Box>

      {codesType === PromocodesOfferType.Global ? (
        <PromocodesGlobalConfiguration values={configurationValues} />
      ) : (
        <PromocodesIndividualConfiguration
          promoCodeGroups={promoCodeGroups}
          offerId={offerId}
          onRefetchOffer={onRefetchOffer}
          isRefetching={isRefetching}
          onBeforeFileUpload={handleBeforeFileUpload}
          onAfterFileUploaded={handleOfferPromocodesChanged}
          onGroupDeleted={handleOfferPromocodesChanged}
        />
      )}
    </Stack>
  );
};
